import { ReactElement, forwardRef } from 'react'
import { colors } from 'const/colors'
import { Button as ButtonCL } from 'syf-component-library/ui/atoms'
import { ButtonProps as CLButtonProps } from 'syf-component-library/ui/atoms/Button/typings'
import styled, { FlattenSimpleInterpolation, css } from 'styled-components'
import mediaQueries, { MediaQuery } from 'mediaQueries'

/**
 * Helper to prevent nested template literals in the FocusableButton styled component.
 * Returns the styles for the disabled state of the button
 */
const getDisabledStyles = (): FlattenSimpleInterpolation => {
  return css`
    color: ${colors.darkGrey};
    background-color: ${colors.greyEightyEight};
    border: none;
    cursor: not-allowed;

    :hover {
      background-color: ${colors.greyEightyEight};
    }
  `
}

const FocusableButton = styled(ButtonCL)<{ isDisabled?: boolean }>`
  ${({ isDisabled }) => isDisabled && getDisabledStyles()}
`

export interface ButtonProps extends CLButtonProps {
  isDisabled?: boolean
}

/**
 * Overriding the CL Button to allow focus when the button is disabled
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, isDisabled, onClick, ...nativeProps }, ref): ReactElement => {
    return (
      <FocusableButton
        className={className}
        ref={ref}
        isDisabled={isDisabled}
        aria-disabled={isDisabled}
        onClick={isDisabled ? null : onClick}
        {...nativeProps}
      />
    )
  }
)

/**
 * Implements responsive css for CML Child Design System button specs.
 *
 * Button will be 100% width except for media query argument. The media query
 * argument will show the desktop width which is determined by content size and
 * based on the CML Child design system.
 *
 */
export const cmlResponsiveButtonCss = (mediaQuery: MediaQuery) => {
  return css`
    /* Use CML Child Design System Specs */
    margin: 0;
    height: auto;
    line-height: 20px;
    font-size: 14px;
    width: 100%;
    min-width: 0px;

    /* 56 padding is for button without icon. 8px Gap + 24px space for icon +
     * 24px padding" is what is in the spec on Figma. Padding left or right
     * should be reduced by 24px when an icon is included in the button. */
    padding: 12px 56px;

    @media ${mediaQueries[mediaQuery]} {
      width: initial;
    }
  `
}

export default Button
